import { useMemo, useState } from 'react'
import EventsLayout from '@/components/layouts/EventLayout'
import FixedBottomActions from '@/components/layouts/events/FixedBottomActions'
import { PageHeader } from '@/components/patterns'
import { Form, Select, Tag, Typography, message, Modal, Input, Tooltip, Segmented, Upload } from 'antd'
import { Button } from '@/components/ui'
import {
  SendOutlined,
  QrcodeOutlined,
  PictureOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  FontSizeOutlined,
  UploadOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import { usePage, router } from '@inertiajs/react'
import { type MergeTagOptions } from '@/lib/mergeTags'
import { api } from '@/lib/apiClient'
import { type AutomatedWhatsappTemplate } from '../types'
import {
  type HeaderImageType,
  type CustomImageMode,
  MAX_HEADER_IMAGE_SIZE_MB,
  ACCEPTED_IMAGE_TYPES,
  titleize,
  labelForMergeTagValue,
  buildSelectOptionGroups,
  renderPreview,
  detectParameterSlots,
  headerTextHasPlaceholder,
  renderHeaderTextPreview,
  unmappedSlots,
} from '../custom/helpers'
import '@/styles/compact-form.css'

const { Text } = Typography

type PageProps = {
  preset: AutomatedWhatsappTemplate
  event: { id: number; slug: string }
  mergeTagOptions: MergeTagOptions
}

// ---------------------------------------------------------------------------
// WhatsApp pricing reference (USD per message)
// ---------------------------------------------------------------------------

type WhatsAppCategory = 'utility' | 'marketing' | 'authentication'

const WHATSAPP_PRICING_USD: Record<WhatsAppCategory, number> = {
  utility: 0.02,
  marketing: 0.0732,
  authentication: 0.0135,
}

const getPricingCost = (category: string | undefined | null): string | null => {
  if (!category) return null
  const key = category.toLowerCase() as WhatsAppCategory
  const cost = WHATSAPP_PRICING_USD[key]
  if (cost === undefined) return null
  return `~$${cost.toFixed(4)} USD`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PresetTemplateEdit = () => {
  const { preset, event, mergeTagOptions } = usePage<PageProps>().props

  const [parameterValues, setParameterValues] = useState<Record<string, string>>(
    preset.parameterValues ?? {},
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Header image state
  const initialHeaderImageValue = preset.parameterValues?.header_image ?? ''
  const initialHeaderImageType: HeaderImageType =
    initialHeaderImageValue === 'attendee.qr_code' || initialHeaderImageValue === ''
      ? 'attendee.qr_code'
      : 'custom_url'
  const initialHeaderImageUrl =
    initialHeaderImageType === 'custom_url' ? initialHeaderImageValue : ''

  const [headerImageType, setHeaderImageType] = useState<HeaderImageType>(initialHeaderImageType)
  const [headerImageUrl, setHeaderImageUrl] = useState(initialHeaderImageUrl)
  const [customImageMode, setCustomImageMode] = useState<CustomImageMode>('upload')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState<string | null>(null)

  const handleImageUpload = async (file: File) => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      message.error('File must be a PNG, JPEG, or WebP image')
      return false
    }
    if (file.size / 1024 / 1024 >= MAX_HEADER_IMAGE_SIZE_MB) {
      message.error(`File must be smaller than ${MAX_HEADER_IMAGE_SIZE_MB}MB`)
      return false
    }

    setIsUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const data = await api.post<{ success: boolean; url: string }>(
        `/events/${event.slug}/messaging/whatsapp_blasts/upload_header_image`,
        formData,
      )
      const url = data.url
      setHeaderImageUrl(url)
      setUploadPreviewUrl(URL.createObjectURL(file))
      message.success('Image uploaded successfully')
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : 'Failed to upload image',
      )
    } finally {
      setIsUploading(false)
    }
    return false // prevent antd auto upload
  }

  const handleRemoveUploadedImage = () => {
    setHeaderImageUrl('')
    setUploadPreviewUrl(null)
  }

  // Header text state
  const [headerTextValue, setHeaderTextValue] = useState<string>(
    preset.parameterValues?.header_text ?? '',
  )

  // Send Test modal state
  const [sendTestOpen, setSendTestOpen] = useState(false)
  const [testPhone, setTestPhone] = useState('')
  const [isSendingTest, setIsSendingTest] = useState(false)

  const templateBody = preset.templateBody ?? ''

  // Build Select option groups from mergeTagOptions
  const selectOptions = useMemo(
    () => buildSelectOptionGroups(mergeTagOptions),
    [mergeTagOptions],
  )

  const previewText = useMemo(
    () =>
      templateBody
        ? renderPreview(templateBody, parameterValues, mergeTagOptions)
        : 'No template body available.',
    [templateBody, parameterValues, mergeTagOptions],
  )

  const buildParameterValuesPayload = (): Record<string, string> => {
    const payload = { ...parameterValues }

    if (preset.hasHeaderImage) {
      const headerImageValue =
        headerImageType === 'attendee.qr_code' ? 'attendee.qr_code' : headerImageUrl.trim()
      if (headerImageValue) {
        payload.header_image = headerImageValue
      }
    }

    if (preset.hasHeaderText && preset.headerText) {
      if (headerTextHasPlaceholder(preset.headerText) && headerTextValue.trim()) {
        payload.header_text = headerTextValue.trim()
      }
    }

    return payload
  }

  const handleParameterChange = (slot: number, value: string) => {
    setParameterValues((prev) => ({ ...prev, [String(slot)]: value }))
  }

  // Meta matches body parameters by position and rejects the whole send when one
  // is missing, so an unmapped slot is caught here rather than at delivery.
  const warnAboutUnmappedSlots = (): boolean => {
    const missing = unmappedSlots(allSlots, parameterValues)
    if (missing.length === 0) return false

    message.error(
      `Map every variable first: ${missing.map((slot) => `{{${slot}}}`).join(', ')}. ` +
        'Pick "Leave blank" for any the event has no value for.',
    )
    return true
  }

  const handleSubmit = () => {
    if (warnAboutUnmappedSlots()) return

    setIsSubmitting(true)

    router.patch(
      `/events/${event.slug}/messaging/whatsapp_blasts/preset/${preset.id}`,
      {
        automated_whatsapp_template: {
          enabled: preset.enabled,
          parameter_values: buildParameterValuesPayload(),
        },
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          message.success('Preset updated')
          router.visit(`/events/${event.slug}/messaging/whatsapp_blasts`, {
            preserveScroll: true,
          })
        },
        onError: () => {
          message.error('Failed to save preset. Please try again.')
          setIsSubmitting(false)
        },
        onFinish: () => setIsSubmitting(false),
      },
    )
  }

  const handleCancel = () => {
    router.visit(`/events/${event.slug}/messaging/whatsapp_blasts`)
  }

  const handleSendTest = async () => {
    const trimmed = testPhone.trim()
    if (!trimmed) {
      message.error('Please enter a phone number.')
      return
    }
    if (warnAboutUnmappedSlots()) return

    setIsSendingTest(true)
    try {
      await api.post(
        `/events/${event.slug}/messaging/whatsapp_blasts/preset/${preset.id}/send_test`,
        {
          phone_number: trimmed,
          parameter_values: buildParameterValuesPayload(),
        },
      )
      message.success(`Test message sent to ${trimmed}`)
      setSendTestOpen(false)
      setTestPhone('')
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : 'Failed to send test message.',
      )
    } finally {
      setIsSendingTest(false)
    }
  }

  // Only show parameter slots that exist in the template body — no extras
  const allSlots = useMemo(() => {
    return detectParameterSlots(templateBody)
  }, [templateBody])

  return (
    <EventsLayout>
      <PageHeader
        title={titleize(preset.templateType)}
        breadcrumb={[
          {
            title: 'WhatsApp Blast',
            href: `/events/${event.slug}/messaging/whatsapp_blasts`,
          },
          { title: 'Edit Preset' },
        ]}
        showBackButton
        onBack={handleCancel}
      />

      <div className="flex gap-6 px-14 py-6 pb-28">
        {/* ----------------------------------------------------------------
            Left panel — Configuration
        ---------------------------------------------------------------- */}
        <div className="w-[440px] flex-shrink-0 space-y-5">
          {/* Template info card */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-800">
                  {titleize(preset.templateType)}
                </div>
                <Text type="secondary" className="!text-xs">
                  <span className="font-mono">{preset.metaTemplateName}</span>
                </Text>
              </div>
              <div className="flex items-center gap-2">
                {preset.category && (
                  <Tag
                    color={
                      preset.category.toLowerCase() === 'utility'
                        ? 'blue'
                        : preset.category.toLowerCase() === 'marketing'
                          ? 'orange'
                          : 'green'
                    }
                    className="!text-[10px] uppercase !m-0"
                  >
                    {preset.category}
                  </Tag>
                )}
                {getPricingCost(preset.category) && (
                  <Tooltip title={`Estimated cost per message: ${getPricingCost(preset.category)}`}>
                    <span className="cursor-help text-[11px] text-gray-400">
                      <InfoCircleOutlined className="mr-0.5" />
                      {getPricingCost(preset.category)}/msg
                    </span>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>

          {/* Header Image Mapping */}
          {preset.hasHeaderImage && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center gap-2">
                <PictureOutlined className="text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Header Image</span>
              </div>

              <Select<HeaderImageType>
                className="w-full"
                value={headerImageType}
                onChange={(val) => {
                  setHeaderImageType(val)
                  if (val === 'attendee.qr_code') setHeaderImageUrl('')
                }}
                options={[
                  {
                    label: (
                      <span className="flex items-center gap-2">
                        <QrcodeOutlined /> Attendee QR Code
                      </span>
                    ),
                    value: 'attendee.qr_code',
                  },
                  {
                    label: (
                      <span className="flex items-center gap-2">
                        <LinkOutlined /> Custom Image URL
                      </span>
                    ),
                    value: 'custom_url',
                  },
                ]}
              />

              {headerImageType === 'custom_url' && (
                <div className="mt-3 space-y-2">
                  <Segmented
                    block
                    size="small"
                    value={customImageMode}
                    onChange={(val) => {
                      setCustomImageMode(val as CustomImageMode)
                      if (val === 'upload') setHeaderImageUrl(uploadPreviewUrl ? headerImageUrl : '')
                    }}
                    options={[
                      { label: 'Upload File', value: 'upload', icon: <UploadOutlined /> },
                      { label: 'Paste URL', value: 'url', icon: <LinkOutlined /> },
                    ]}
                  />

                  {customImageMode === 'url' ? (
                    <Input
                      placeholder="https://example.com/image.png"
                      value={headerImageUrl}
                      onChange={(e) => setHeaderImageUrl(e.target.value)}
                      allowClear
                    />
                  ) : uploadPreviewUrl || (headerImageUrl && !headerImageUrl.startsWith('attendee.')) ? (
                    <div className="relative rounded-lg overflow-hidden border border-gray-200">
                      <img
                        src={uploadPreviewUrl || headerImageUrl}
                        alt="Header preview"
                        className="w-full h-32 object-cover"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveUploadedImage}
                        className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
                      >
                        <DeleteOutlined className="text-red-500 text-xs" />
                      </button>
                    </div>
                  ) : (
                    <Upload.Dragger
                      showUploadList={false}
                      beforeUpload={handleImageUpload}
                      accept=".png,.jpg,.jpeg,.webp"
                      disabled={isUploading}
                      className="!border-gray-200 !bg-gray-50/50"
                    >
                      <div className="flex flex-col items-center py-2">
                        {isUploading ? (
                          <span className="text-xs text-gray-500">Uploading...</span>
                        ) : (
                          <>
                            <UploadOutlined className="text-xl text-gray-400 mb-1" />
                            <span className="text-xs text-gray-500">Click or drag image here</span>
                          </>
                        )}
                      </div>
                    </Upload.Dragger>
                  )}
                </div>
              )}

              <div className="mt-2 text-[11px] text-gray-400">
                {headerImageType === 'attendee.qr_code'
                  ? 'A unique QR code image will be generated for each attendee.'
                  : headerImageUrl.trim()
                    ? 'This image will be used for all recipients.'
                    : `Upload or paste a publicly accessible image URL. Max ${MAX_HEADER_IMAGE_SIZE_MB}MB (PNG, JPEG, WebP).`}
              </div>
            </div>
          )}

          {/* Header Text Mapping */}
          {preset.hasHeaderText && preset.headerText && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center gap-2">
                <FontSizeOutlined className="text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Header Text</span>
              </div>

              <div className="mb-3 rounded bg-gray-50 px-3 py-2 font-mono text-[13px] text-gray-700">
                {preset.headerText}
              </div>

              {headerTextHasPlaceholder(preset.headerText) ? (
                <>
                  <div className="mb-1.5 text-xs text-gray-500">
                    Map <span className="font-mono">{'{{1}}'}</span> to a field:
                  </div>
                  <Select
                    className="w-full"
                    placeholder="Select a field..."
                    value={headerTextValue || undefined}
                    onChange={(val: string) => setHeaderTextValue(val)}
                    options={selectOptions}
                    showSearch
                    allowClear
                    onClear={() => setHeaderTextValue('')}
                    filterOption={(input, option) =>
                      (option?.label as string)
                        ?.toLowerCase()
                        .includes(input.toLowerCase()) ?? false
                    }
                  />
                  <div className="mt-2 text-[11px] text-gray-400">
                    The selected value will replace the placeholder in the header.
                  </div>
                </>
              ) : (
                <div className="text-[11px] text-gray-400">
                  This header text has no placeholders and will be sent as-is.
                </div>
              )}
            </div>
          )}

          {/* Variable Mapping */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-1 text-sm font-medium text-gray-700">Variable Mapping</div>
            <div className="mb-4 text-xs text-gray-400">
              Map each template placeholder to attendee or event data.
            </div>

            {allSlots.length === 0 ? (
              <div className="rounded border border-dashed border-gray-200 p-4 text-center text-xs text-gray-400">
                No parameters detected in this template.
              </div>
            ) : (
              <div className="space-y-3">
                {allSlots.map((slot) => (
                  <div key={slot}>
                    <div className="mb-1 flex items-center gap-2">
                      <Tag className="!m-0 !text-[11px] font-mono !bg-gray-50 !border-gray-200 !text-gray-600">
                        {`{{${slot}}}`}
                      </Tag>
                      {parameterValues[String(slot)] && (
                        <span className="text-[11px] text-gray-400">
                          → {labelForMergeTagValue(parameterValues[String(slot)], mergeTagOptions)}
                        </span>
                      )}
                    </div>
                    <Select
                      className="w-full"
                      placeholder="Select a field..."
                      value={parameterValues[String(slot)] || undefined}
                      onChange={(val) => handleParameterChange(slot, val)}
                      options={selectOptions}
                      showSearch
                      allowClear
                      filterOption={(input, option) =>
                        (option?.label as string)
                          ?.toLowerCase()
                          .includes(input.toLowerCase()) ?? false
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ----------------------------------------------------------------
            Right panel — Preview
        ---------------------------------------------------------------- */}
        <div className="flex-1 min-w-0">
          <div className="sticky top-6">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Message Preview</span>
              <Button
                variant="secondary"
                size="small"
                icon={<SendOutlined />}
                onClick={() => setSendTestOpen(true)}
              >
                Send Test
              </Button>
            </div>

            {/* WhatsApp chat bubble */}
            <div className="rounded-xl bg-[#e5ddd5] p-4">
              <div className="mb-3 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-full bg-[#128C7E] text-[10px] font-bold text-white">
                  WA
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-700">AVA Events</div>
                  <div className="text-[10px] text-gray-500">Automated message</div>
                </div>
              </div>

              <div className="max-w-[360px] overflow-hidden rounded-lg rounded-tl-none bg-white shadow-sm">
                {/* Header image */}
                {preset.hasHeaderImage && (
                  <div className="flex h-36 w-full items-center justify-center bg-gray-100">
                    {headerImageType === 'attendee.qr_code' ? (
                      <div className="flex flex-col items-center gap-1 text-gray-400">
                        <QrcodeOutlined style={{ fontSize: 36 }} />
                        <span className="text-[10px]">QR Code</span>
                      </div>
                    ) : headerImageUrl.trim() ? (
                      <img
                        src={headerImageUrl.trim()}
                        alt="Header"
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                          e.currentTarget.nextElementSibling?.removeAttribute('hidden')
                        }}
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-gray-300">
                        <PictureOutlined style={{ fontSize: 36 }} />
                        <span className="text-[10px]">No image</span>
                      </div>
                    )}
                    <div hidden className="flex flex-col items-center gap-1 text-gray-300">
                      <PictureOutlined style={{ fontSize: 36 }} />
                      <span className="text-[10px]">Image unavailable</span>
                    </div>
                  </div>
                )}

                {/* Header text */}
                {preset.hasHeaderText && preset.headerText && (
                  <div className="border-b border-gray-100 px-3 pt-3 pb-2">
                    <div className="text-[14px] font-semibold leading-snug text-gray-900">
                      {renderHeaderTextPreview(
                        preset.headerText,
                        headerTextValue || undefined,
                        mergeTagOptions,
                      )}
                    </div>
                  </div>
                )}

                {/* Body */}
                <div className="p-3">
                  <div className="whitespace-pre-wrap text-[13px] leading-relaxed text-gray-800">
                    {previewText}
                  </div>
                  <div className="mt-2 text-right text-[10px] text-gray-400">
                    {new Date().toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Mapping summary */}
            {(allSlots.length > 0 || preset.hasHeaderImage || preset.hasHeaderText) && (
              <div className="mt-3 rounded-lg border border-gray-200 bg-white px-3 py-2">
                <div className="mb-1.5 text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                  Mapping Summary
                </div>
                <div className="space-y-1">
                  {preset.hasHeaderImage && (
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <span className="font-mono text-gray-500">header_image</span>
                      <span className="text-gray-300">→</span>
                      <span className="text-gray-600">
                        {headerImageType === 'attendee.qr_code'
                          ? 'Attendee QR Code'
                          : headerImageUrl.trim() || '—'}
                      </span>
                    </div>
                  )}
                  {preset.hasHeaderText && preset.headerText && headerTextHasPlaceholder(preset.headerText) && (
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <span className="font-mono text-gray-500">header_text</span>
                      <span className="text-gray-300">→</span>
                      <span className={headerTextValue ? 'text-gray-600' : 'italic text-gray-300'}>
                        {headerTextValue
                          ? labelForMergeTagValue(headerTextValue, mergeTagOptions)
                          : 'not mapped'}
                      </span>
                    </div>
                  )}
                  {allSlots.map((slot) => {
                    const val = parameterValues[String(slot)]
                    return (
                      <div key={slot} className="flex items-center gap-1.5 text-[11px]">
                        <span className="font-mono text-gray-500">{`{{${slot}}}`}</span>
                        <span className="text-gray-300">→</span>
                        <span className={val ? 'text-gray-600' : 'italic text-gray-300'}>
                          {val ? labelForMergeTagValue(val, mergeTagOptions) : 'not mapped'}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Send Test modal */}
      <Modal
        title="Send Test WhatsApp"
        open={sendTestOpen}
        onCancel={() => {
          setSendTestOpen(false)
          setTestPhone('')
        }}
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setSendTestOpen(false)
                setTestPhone('')
              }}
              disabled={isSendingTest}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              icon={<SendOutlined />}
              onClick={handleSendTest}
              loading={isSendingTest}
            >
              Send Test
            </Button>
          </div>
        }
        width={420}
        centered
      >
        <div className="py-2">
          <p className="mb-4 text-sm text-black/65">
            Send a test message using the current parameter mapping.
          </p>
          <label className="mb-2 block text-sm font-medium text-black/85">
            Phone number
          </label>
          <Input
            placeholder="+60123456789"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            onPressEnter={handleSendTest}
            disabled={isSendingTest}
            size="middle"
          />
          <p className="mt-2 text-xs text-black/45">
            Include the country code (e.g. +60 for Malaysia).
          </p>
        </div>
      </Modal>

      <FixedBottomActions
        onCancel={handleCancel}
        submitText="Save Changes"
        submitLoading={isSubmitting}
        rightActions={
          <Button variant="primary" onClick={handleSubmit} loading={isSubmitting}>
            Save Changes
          </Button>
        }
      />
    </EventsLayout>
  )
}

export default PresetTemplateEdit
